import { Injectable } from '@angular/core'

export interface SpriteIconData {
  x: number
  y: number
  width: number
  height: number
  spriteUrl: string
  spriteWidth: number
  spriteHeight: number
}

@Injectable({
  providedIn: 'root',
})
export class SpritePositionService {
  private spriteMetadata: { [size: string]: any } = {}
  private spriteCache = new Map<string, SpriteIconData>()

  /**
   * Get sprite position data for CSS background positioning
   * Optimized for Azure Static Web Apps CDN caching
   */
  async getSpritePosition(
    iconName: string,
    preferredSize: 'small' | 'medium' | 'large' = 'large',
  ): Promise<SpriteIconData | null> {
    // Check cache first (Azure CDN optimization)
    const cacheKey = `${iconName}_${preferredSize}`
    if (this.spriteCache.has(cacheKey)) {
      return this.spriteCache.get(cacheKey)!
    }

    try {
      // Try preferred size first
      let spriteData = await this.loadSpriteData(preferredSize)
      let iconData = this.findIconInSprite(iconName, spriteData, preferredSize)

      if (iconData) {
        this.spriteCache.set(cacheKey, iconData)
        return iconData
      }

      // Try other sizes as fallback
      const sizes: Array<'large' | 'medium' | 'small'> = ['large', 'medium', 'small']
      for (const size of sizes) {
        if (size === preferredSize) continue

        spriteData = await this.loadSpriteData(size)
        iconData = this.findIconInSprite(iconName, spriteData, size)

        if (iconData) {
          this.spriteCache.set(cacheKey, iconData)
          return iconData
        }
      }

      return null
    } catch (error) {
      console.warn(`Failed to get sprite position for ${iconName}:`, error)
      return null
    }
  }

  /**
   * Load sprite metadata from Azure CDN
   */
  private async loadSpriteData(size: 'small' | 'medium' | 'large'): Promise<any> {
    if (this.spriteMetadata[size]) {
      return this.spriteMetadata[size]
    }

    try {
      const spriteName = size === 'large' ? 'traffic-signs-large' : `traffic-signs-${size}`
      const response = await fetch(`/assets/sprites/${size}/${spriteName}.json`)

      if (!response.ok) {
        throw new Error(`Failed to load sprite metadata for ${size}: ${response.status}`)
      }

      this.spriteMetadata[size] = await response.json()
      return this.spriteMetadata[size]
    } catch (error) {
      console.warn(`Failed to load sprite metadata for ${size}:`, error)
      return null
    }
  }

  /**
   * Find icon in sprite metadata and calculate position
   */
  private findIconInSprite(
    iconName: string,
    spriteData: any,
    size: 'small' | 'medium' | 'large',
  ): SpriteIconData | null {
    if (!spriteData) return null

    // Try different icon name variations
    const iconVariants = [
      iconName, // Large (no suffix)
      `${iconName}_MEDIUM`, // Medium
      `${iconName}_SMALL`, // Small
    ]

    for (const variant of iconVariants) {
      if (spriteData[variant]) {
        const icon = spriteData[variant]
        const spriteName = size === 'large' ? 'traffic-signs-large' : `traffic-signs-${size}`
        return {
          x: icon.x,
          y: icon.y,
          width: icon.width,
          height: icon.height,
          spriteUrl: `/assets/sprites/${size}/${spriteName}.png`,
          spriteWidth: this.calculateSpriteWidth(spriteData),
          spriteHeight: this.calculateSpriteHeight(spriteData),
        }
      }
    }

    return null
  }

  /**
   * Calculate total sprite dimensions for CSS background-size
   */
  private calculateSpriteWidth(spriteData: any): number {
    let maxX = 0
    let maxWidth = 0

    Object.values(spriteData).forEach((icon: any) => {
      const rightEdge = icon.x + icon.width
      if (rightEdge > maxX + maxWidth) {
        maxX = icon.x
        maxWidth = icon.width
      }
    })

    return maxX + maxWidth
  }

  private calculateSpriteHeight(spriteData: any): number {
    let maxY = 0
    let maxHeight = 0

    Object.values(spriteData).forEach((icon: any) => {
      const bottomEdge = icon.y + icon.height
      if (bottomEdge > maxY + maxHeight) {
        maxY = icon.y
        maxHeight = icon.height
      }
    })

    return maxY + maxHeight
  }

  /**
   * Clear cache for memory optimization
   */
  clearCache(): void {
    this.spriteCache.clear()
  }

  /**
   * Get cache stats for Azure monitoring
   */
  getCacheStats(): { size: number; keys: string[] } {
    return {
      size: this.spriteCache.size,
      keys: Array.from(this.spriteCache.keys()),
    }
  }
}
